import React, { useEffect, useState, useCallback } from "react";
import {
  maintenanceApi,
  VehicleCompany,
  VehicleModel,
  VehicleCompanyOption,
  VehicleTypeOption,
  VehicleSocketType,
} from "../../../api/maintenance";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { canEditMaintenance } from "../shared/MaintenanceGuard";
import VehicleCompanyForm from "./VehicleCompanyForm";
import VehicleModelForm from "./VehicleModelForm";
import {
  Plus, Search, Edit2, Trash2, Car,
  ChevronLeft, ChevronRight, ChevronRight as Caret,
} from "lucide-react";
import "./maintenance-vehicles.scss";
import { useNotice } from "../shared/useNotice";

const PAGE_SIZE = 20;

const VehicleManagement: React.FC = () => {
  // Lookups (loaded once, for the model form)
  const [companyOptions, setCompanyOptions] = useState<VehicleCompanyOption[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const [socketTypes, setSocketTypes] = useState<VehicleSocketType[]>([]);

  // Companies (left pane)
  const [companies, setCompanies] = useState<VehicleCompany[]>([]);
  const [companyTotal, setCompanyTotal] = useState(0);
  const [companyPage, setCompanyPage] = useState(1);
  const [companyTotalPages, setCompanyTotalPages] = useState(0);
  const [companySearch, setCompanySearch] = useState("");
  const [companySearchInput, setCompanySearchInput] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Selected company + its models (right pane)
  const [selected, setSelected] = useState<VehicleCompany | null>(null);
  const [models, setModels] = useState<VehicleModel[]>([]);
  const [modelTotal, setModelTotal] = useState(0);
  const [modelPage, setModelPage] = useState(1);
  const [modelTotalPages, setModelTotalPages] = useState(0);
  const [modelSearch, setModelSearch] = useState("");
  const [modelSearchInput, setModelSearchInput] = useState("");
  const [loadingModels, setLoadingModels] = useState(false);

  // Modals + banners
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState<VehicleCompany | null>(null);
  const [showModelForm, setShowModelForm] = useState(false);
  const [editingModel, setEditingModel] = useState<VehicleModel | null>(null);
  const [message, setMessage] = useNotice("success", "");
  const [error, setError] = useNotice("error", "");

  useEffect(() => {
    Promise.all([
      maintenanceApi.getVehicleCompaniesDropdown(),
      maintenanceApi.getVehicleTypeOptions(),
      maintenanceApi.getVehicleSocketTypes(),
    ]).then(([co, vt, st]) => {
      setCompanyOptions(co.data);
      setVehicleTypes(vt.data);
      setSocketTypes(st.data);
    }).catch(console.error);
  }, []);

  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const params: Record<string, any> = { page: companyPage, page_size: PAGE_SIZE };
      if (companySearch.trim()) params.search = companySearch.trim();
      const res = await maintenanceApi.getVehicleCompanies(params);
      setCompanies(res.data.items);
      setCompanyTotal(res.data.total);
      setCompanyTotalPages(res.data.total_pages);
    } catch (err) {
      console.error("Failed to fetch companies:", err);
      setError("Failed to load vehicle companies");
    } finally {
      setLoadingCompanies(false);
    }
  }, [companyPage, companySearch]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const fetchModels = useCallback(async () => {
    if (!selected) {
      setModels([]);
      return;
    }
    setLoadingModels(true);
    try {
      const params: Record<string, any> = {
        company_id: selected.id, page: modelPage, page_size: PAGE_SIZE,
      };
      if (modelSearch.trim()) params.search = modelSearch.trim();
      const res = await maintenanceApi.getVehicleModels(params);
      setModels(res.data.items);
      setModelTotal(res.data.total);
      setModelTotalPages(res.data.total_pages);
    } catch (err) {
      console.error("Failed to fetch models:", err);
      setError("Failed to load vehicle models");
    } finally {
      setLoadingModels(false);
    }
  }, [selected, modelPage, modelSearch]);

  useEffect(() => { fetchModels(); }, [fetchModels]);

  const refreshLookups = () =>
    maintenanceApi.getVehicleCompaniesDropdown()
      .then((r) => setCompanyOptions(r.data))
      .catch(console.error);

  const selectCompany = (c: VehicleCompany) => {
    setSelected(c);
    setModelPage(1);
    setModelSearch("");
    setModelSearchInput("");
    setError("");
  };

  // ── Company actions ────────────────────────────────────────────────
  const handleCompanySearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCompanyPage(1);
    setCompanySearch(companySearchInput);
  };

  const handleAddCompany = () => { setEditingCompany(null); setShowCompanyForm(true); };
  const handleEditCompany = (c: VehicleCompany, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCompany(c);
    setShowCompanyForm(true);
  };

  const handleDeleteCompany = async (c: VehicleCompany, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm(`Delete company "${c.name}"?`)) return;
    try {
      await maintenanceApi.deleteVehicleCompany(c.id);
      setMessage(`Company "${c.name}" deleted`);
      setError("");
      if (selected?.id === c.id) setSelected(null);
      fetchCompanies();
      refreshLookups();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to delete company");
      setMessage("");
    }
  };

  const handleCompanyFormDone = () => {
    setShowCompanyForm(false);
    setMessage(editingCompany ? "Company updated" : "Company created");
    setError("");
    setEditingCompany(null);
    fetchCompanies();
    refreshLookups();
  };

  // ── Model actions ──────────────────────────────────────────────────
  const handleModelSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setModelPage(1);
    setModelSearch(modelSearchInput);
  };

  const handleAddModel = () => { setEditingModel(null); setShowModelForm(true); };
  const handleEditModel = (m: VehicleModel) => { setEditingModel(m); setShowModelForm(true); };

  const handleDeleteModel = async (m: VehicleModel) => {
    if (!window.confirm(`Delete model "${m.model_name}"?`)) return;
    try {
      await maintenanceApi.deleteVehicleModel(m.id);
      setMessage(`Model "${m.model_name}" deleted`);
      setError("");
      fetchModels();
      fetchCompanies();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to delete model");
      setMessage("");
    }
  };

  const handleModelFormDone = () => {
    setShowModelForm(false);
    setMessage(editingModel ? "Model updated" : "Model created");
    setError("");
    setEditingModel(null);
    fetchModels();
    fetchCompanies();
  };

  return (
    <MaintenanceLayout title="Vehicles (Maintenance)">
      <div className="maint-stations">
        {message && <p className="maint-success">{message}</p>}
        {error && <p className="maint-form-error">{error}</p>}

        {showCompanyForm && (
          <VehicleCompanyForm
            editingCompany={editingCompany}
            onClose={() => { setShowCompanyForm(false); setEditingCompany(null); }}
            onDone={handleCompanyFormDone}
          />
        )}
        {showModelForm && (
          <VehicleModelForm
            companies={companyOptions}
            vehicleTypes={vehicleTypes}
            socketTypes={socketTypes}
            editingModel={editingModel}
            defaultCompanyId={selected?.id}
            onClose={() => { setShowModelForm(false); setEditingModel(null); }}
            onDone={handleModelFormDone}
          />
        )}

        <div className="maint-veh-split">
          {/* ── Companies pane ── */}
          <div className="maint-veh-pane maint-veh-pane--companies">
            <div className="maint-veh-pane-head">
              <h3><Car size={17} /> Companies <span className="maint-veh-count">{companyTotal}</span></h3>
              {canEditMaintenance() && (
                <button className="maint-btn-primary" onClick={handleAddCompany}>
                  <Plus size={15} /> New
                </button>
              )}
            </div>

            <form onSubmit={handleCompanySearch} className="maint-search" style={{ maxWidth: "none" }}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Search companies..."
                value={companySearchInput}
                onChange={(e) => setCompanySearchInput(e.target.value)}
              />
            </form>

            {loadingCompanies ? (
              <p className="maint-loading">Loading...</p>
            ) : (
              <>
                <ul className="maint-veh-company-list">
                  {companies.map((c) => (
                    <li
                      key={c.id}
                      className={`maint-veh-company ${selected?.id === c.id ? "active" : ""}`}
                      onClick={() => selectCompany(c)}
                    >
                      <span className="maint-veh-company-name">{c.name}</span>
                      <span className="maint-veh-badge">{c.model_count} models</span>
                      {canEditMaintenance() && (
                        <span className="maint-veh-actions">
                          <button title="Edit" onClick={(e) => handleEditCompany(c, e)}><Edit2 size={14} /></button>
                          <button title="Delete" className="danger" onClick={(e) => handleDeleteCompany(c, e)}><Trash2 size={14} /></button>
                        </span>
                      )}
                      <Caret size={15} color="#bbb" />
                    </li>
                  ))}
                  {companies.length === 0 && (
                    <li className="maint-empty" style={{ listStyle: "none" }}>No companies found</li>
                  )}
                </ul>

                {companyTotalPages > 1 && (
                  <div className="maint-pagination">
                    <span>{companyTotal} total</span>
                    <div className="maint-pagination-controls">
                      <button disabled={companyPage <= 1} onClick={() => setCompanyPage(companyPage - 1)}><ChevronLeft size={16} /></button>
                      <span>Page {companyPage} of {companyTotalPages}</span>
                      <button disabled={companyPage >= companyTotalPages} onClick={() => setCompanyPage(companyPage + 1)}><ChevronRight size={16} /></button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ── Models pane ── */}
          <div className="maint-veh-pane maint-veh-pane--models">
            {!selected ? (
              <p className="maint-veh-placeholder">
                Select a company on the left to view and manage its models
              </p>
            ) : (
              <>
                <div className="maint-veh-pane-head">
                  <h3>
                    {selected.name} <Caret size={14} color="#999" /> Models
                    <span className="maint-veh-count">{modelTotal}</span>
                  </h3>
                  {canEditMaintenance() && (
                    <button className="maint-btn-primary" onClick={handleAddModel}>
                      <Plus size={15} /> New model
                    </button>
                  )}
                </div>

                <form onSubmit={handleModelSearch} className="maint-search" style={{ maxWidth: "none" }}>
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder={`Search models in ${selected.name}...`}
                    value={modelSearchInput}
                    onChange={(e) => setModelSearchInput(e.target.value)}
                  />
                </form>

                {loadingModels ? (
                  <p className="maint-loading">Loading...</p>
                ) : (
                  <>
                    <table className="maint-table" style={{ marginTop: "12px" }}>
                      <thead>
                        <tr>
                          <th>Model</th>
                          <th>Type</th>
                          <th>Battery</th>
                          <th>kWh</th>
                          <th>Range (km)</th>
                          <th>Socket</th>
                          <th>Used by</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {models.map((m) => (
                          <tr key={m.id}>
                            <td style={{ fontWeight: 600 }}>{m.model_name}</td>
                            <td>{m.vehicle_type_name || "-"}</td>
                            <td>{m.battery_chemistry}</td>
                            <td>{m.rated_capacity_kwh}</td>
                            <td>{m.claimed_full_charge_range}</td>
                            <td>{m.socket_name || "-"}</td>
                            <td>
                              <span className={`maint-veh-used ${m.user_vehicle_count === 0 ? "zero" : ""}`}>
                                {m.user_vehicle_count} user{m.user_vehicle_count === 1 ? "" : "s"}
                              </span>
                            </td>
                            <td>
                              {canEditMaintenance() && (
                                <div style={{ display: "flex", gap: "4px" }}>
                                  <button className="maint-btn-small" onClick={() => handleEditModel(m)}><Edit2 size={14} /></button>
                                  <button className="maint-btn-small" onClick={() => handleDeleteModel(m)} style={{ color: "#c62828" }}><Trash2 size={14} /></button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                        {models.length === 0 && (
                          <tr><td colSpan={8} className="maint-empty">No models for this company yet</td></tr>
                        )}
                      </tbody>
                    </table>

                    {modelTotalPages > 1 && (
                      <div className="maint-pagination">
                        <span>{modelTotal} models total</span>
                        <div className="maint-pagination-controls">
                          <button disabled={modelPage <= 1} onClick={() => setModelPage(modelPage - 1)}><ChevronLeft size={16} /></button>
                          <span>Page {modelPage} of {modelTotalPages}</span>
                          <button disabled={modelPage >= modelTotalPages} onClick={() => setModelPage(modelPage + 1)}><ChevronRight size={16} /></button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </MaintenanceLayout>
  );
};

export default VehicleManagement;
